import { useContext, useState } from 'react'
import { ArrowDropDown, MailOutlined } from '@mui/icons-material'
import { Box, Button, Grid, Menu, MenuItem } from '@mui/material'
import { useSnackbar } from 'notistack'

import ImpersonateInMobileDialog from '../../../../components/impersonate-in-mobile-dialog'
import { useHost } from '../../../../providers/app-provider'
import { useAuthentication } from '../../../../providers/auth-provider'
import Api from '../../../../utils/api'
import { BRANCH_URL, INSIGHTS_URL, PROTECT_LITE_URL } from '../../../../utils/constants'
import PeopleContext from '../../context'

const appMatrix = (user: IUserType | undefined) => ({
  'protect-mobile': {
    appName: 'Protect Mobile App',
    externalApp: 'protect',
    siteUrl: PROTECT_LITE_URL,
    impersonateUrl: `${BRANCH_URL}/impersonate?ref=${user?.id}`,
  },
  'protect-lite': {
    appName: 'Protect Lite',
    externalApp: 'protect',
    siteUrl: PROTECT_LITE_URL,
    impersonateUrl: `${PROTECT_LITE_URL}?userToImpersonate=${user?.id}`,
  },
  insights: {
    appName: 'Insights',
    externalApp: 'insights',
    siteUrl: INSIGHTS_URL,
    impersonateUrl: `${INSIGHTS_URL}?userToImpersonate=${user?.id}`,
  },
  central: {
    appName: 'Central',
    externalApp: 'central',
    siteUrl: '',
    impersonateUrl: '',
  },
})

export const AppsInvitePwResetButton = () => {
  const { enqueueSnackbar } = useSnackbar()

  const { membershipStatus, setMembershipStatus, user } = useContext(PeopleContext)
  const { auth, hasInternalUserAccess } = useAuthentication()
  const host = useHost()

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const emailType = membershipStatus !== 'Deactivated' ? 'reset' : 'invite'

  const resetPassword = async (app: 'protect-mobile' | 'insights' | 'central') => {
    setAnchorEl(null)

    if (!user || !auth.user?.access_token) return
    const { siteUrl, externalApp } = appMatrix(user)[app]

    if (emailType === 'reset') {
      const res = await Api.get(
        `${host}/api/passwords/resetpassword?email=${encodeURIComponent(user.email)}&refererOption=${siteUrl}&externalApp=${externalApp}`
      )

      if (res.ok) {
        enqueueSnackbar('Password reset email sent')
      } else {
        enqueueSnackbar('Something went wrong. Please try again later', { variant: 'error' })
      }
    } else {
      const params = { referToApplication: 'central', userId: String(user.id), externalApp }
      const urlParams = new URLSearchParams(params)

      const res = await Api.post(`${host}/api/authentication/membership?${urlParams.toString()}`, {})
      if (res.ok) {
        setMembershipStatus('Invited')
        enqueueSnackbar(`Invitation email for ${externalApp} was sent.`)
      } else {
        enqueueSnackbar(`Something went wrong. Please try again later`, { variant: 'error' })
      }
    }
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<MailOutlined />}
        endIcon={<ArrowDropDown />}
        onClick={(e) => {
          setAnchorEl(e.currentTarget)
        }}
      >
        {membershipStatus !== 'Deactivated' ? 'PW Reset' : 'Invite'}
      </Button>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => resetPassword('protect-mobile')} aria-label={`Send password ${emailType} email for Protect`}>
          Protect
        </MenuItem>

        {hasInternalUserAccess && (
          <MenuItem onClick={() => resetPassword('insights')} aria-label={`Send password ${emailType} email for Insights`}>
            Insights
          </MenuItem>
        )}

        {hasInternalUserAccess && (
          <MenuItem onClick={() => resetPassword('central')} aria-label={`Send password ${emailType} email for Central`}>
            Central
          </MenuItem>
        )}
      </Menu>
    </>
  )
}

const AppRow = ({ app }: { app: 'protect-mobile' | 'protect-lite' | 'insights' | 'central' }) => {
  const { ownUser, hasInternalUserAccess } = useAuthentication()
  const { user } = useContext(PeopleContext)

  const [impersonateProtectDialog, setImpersonateProtectDialog] = useState<boolean>(false)

  const { appName, impersonateUrl } = appMatrix(user)[app]

  return (
    <Grid container={true} sx={{ margin: '12px 0' }}>
      <Grid size={{ xs: 8, md: 3 }}>{appName}</Grid>
      <Grid size={{ xs: 4, md: 5 }}>{/* TODO: Add Last Login */}</Grid>
      <Grid
        container={true}
        size={{ xs: 5, md: 4 }}
        sx={{
          justifyContent: 'flex-end',
          gap: '34px',
        }}
      >
        {app !== 'protect-mobile' && app !== 'central' && user?.id !== ownUser?.id && hasInternalUserAccess && (
          <Button component="a" href={impersonateUrl} target="_blank" variant="outlined" size="small" aria-label={`Impersonate in ${appName}`}>
            Impersonate
          </Button>
        )}

        {app === 'protect-mobile' && user?.id !== ownUser?.id && hasInternalUserAccess && (
          <Button variant="outlined" size="small" onClick={() => setImpersonateProtectDialog(true)} aria-label={`Impersonate in ${appName}`}>
            Impersonate
          </Button>
        )}

        {user && (
          <ImpersonateInMobileDialog showImpersonateDialog={impersonateProtectDialog} setShowImpersonateDialog={setImpersonateProtectDialog} user={user} />
        )}
      </Grid>
    </Grid>
  )
}

const AppsContent = () => (
  <Box
    sx={{
      padding: '0 18px',
    }}
  >
    <AppRow app="protect-mobile" />
    <AppRow app="protect-lite" />
    {/* TODO uncomment this when Impersonate in Insights is fixed and working , DON'T FORGET TO UNCOMMENT TESTS in app/index.test.tsx file */}
    {/* <AppRow app="insights" /> */}
    <AppRow app="central" />
  </Box>
)

export default AppsContent
